import { useCallback, useState } from 'react';

import type { OtpContext } from '../../../types';
import { ANALYTICS_KEYS, BUCK_EVENTS } from '../../../utils/analytics';
import { OtpError, OtpErrorType, signupWithOtp } from '../../../utils/authApi';
import type { AnalyticsHandler } from './useSignupFlow';

type UseProfileStepOptions = {
  apiBaseUrl: string;
  email: string;
  idpToken: string | null;
  context?: OtpContext;
  onSuccess: (context?: OtpContext) => void;
  onSessionExpired: () => void;
  onAnalyticsEvent?: AnalyticsHandler;
};

type UseProfileStepReturn = {
  firstName: string;
  setFirstName: (value: string) => void;
  lastName: string;
  setLastName: (value: string) => void;
  firstNameError?: string;
  lastNameError?: string;
  signingUp: boolean;
  handleCompleteSignup: () => Promise<void>;
};

export const useProfileStep = ({
  apiBaseUrl,
  email,
  idpToken,
  context,
  onSuccess,
  onSessionExpired,
  onAnalyticsEvent,
}: UseProfileStepOptions): UseProfileStepReturn => {
  const [firstName, setFirstNameValue] = useState('');
  const [lastName, setLastNameValue] = useState('');
  const [firstNameError, setFirstNameError] = useState<string | undefined>();
  const [lastNameError, setLastNameError] = useState<string | undefined>();
  const [signingUp, setSigningUp] = useState(false);

  const setFirstName = useCallback(
    (value: string) => {
      setFirstNameValue(value);
      if (firstNameError) {
        setFirstNameError(undefined);
      }
    },
    [firstNameError]
  );

  const setLastName = useCallback(
    (value: string) => {
      setLastNameValue(value);
      if (lastNameError) {
        setLastNameError(undefined);
      }
    },
    [lastNameError]
  );

  const validateProfile = useCallback((firstNameValue: string, lastNameValue: string): boolean => {
    let hasError = false;

    if (!firstNameValue.trim()) {
      setFirstNameError('First name is required');
      hasError = true;
    } else {
      setFirstNameError(undefined);
    }

    if (!lastNameValue.trim()) {
      setLastNameError('Last name is required');
      hasError = true;
    } else {
      setLastNameError(undefined);
    }

    return !hasError;
  }, []);

  const handleCompleteSignup = useCallback(async () => {
    const isValid = validateProfile(firstName, lastName);
    if (!isValid || !idpToken) {
      return;
    }

    setSigningUp(true);

    try {
      await signupWithOtp(apiBaseUrl, idpToken, {
        email,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      onAnalyticsEvent?.(BUCK_EVENTS.SIGNUP_COMPLETED, {
        [ANALYTICS_KEYS.METHOD]: 'email',
        [ANALYTICS_KEYS.USER_TYPE]: 'new',
        [ANALYTICS_KEYS.LISTING_ID]: context?.listingId,
        [ANALYTICS_KEYS.PACKAGE_ID]: context?.packageId,
      });

      onSuccess(context);
    } catch (error) {
      const otpError = error instanceof OtpError ? error : null;
      const errorMessage = error instanceof Error ? error.message : 'Signup failed';

      onAnalyticsEvent?.(BUCK_EVENTS.OTP_ERROR, {
        [ANALYTICS_KEYS.ERROR_TYPE]: otpError?.type || 'unknown',
        [ANALYTICS_KEYS.ERROR_MESSAGE]: errorMessage,
        [ANALYTICS_KEYS.STEP]: 'complete_signup',
        [ANALYTICS_KEYS.LISTING_ID]: context?.listingId,
        [ANALYTICS_KEYS.PACKAGE_ID]: context?.packageId,
      });

      if (otpError?.type === OtpErrorType.TOKEN_EXPIRED) {
        setFirstNameError('Your session has expired. Please start again.');
        setTimeout(() => {
          onSessionExpired();
        }, 2000);
      } else if (otpError?.type === OtpErrorType.NETWORK_ERROR) {
        setFirstNameError(`${errorMessage} Please try again.`);
      } else {
        setFirstNameError(errorMessage);
      }
    } finally {
      setSigningUp(false);
    }
  }, [
    apiBaseUrl,
    context,
    email,
    firstName,
    idpToken,
    lastName,
    onAnalyticsEvent,
    onSessionExpired,
    onSuccess,
    validateProfile,
  ]);

  return {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    firstNameError,
    lastNameError,
    signingUp,
    handleCompleteSignup,
  };
};
